/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";
import {toInteger} from "../../../../../utils/formValueUtils";

/**
 * HOC for the partner role form
 */
class PartnerRoleForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            partnerRoleId: null,
            partnerRoleName: '',
            partnerId:'',
            partnerName: '',
            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Partner Role Name: *',
                    inputName: 'partnerRoleName',
                    placeholder: 'Please enter partner role name here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Partner *',
                    inputName: 'partnerId',
                    optionValueName: 'partnerId',
                    optionLabelName: 'partnerName',
                    required: true,
                    path: '/partner-roles',
                    valueType: 'partners'
                }
            ]
        };

        // get the parameter from URL
        const partnerRoleId = this.props.match.params.id;

        // set icons to the header
        if (partnerRoleId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'partnerRoles',
                pageName: "Partner Roles",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/partnerRoles",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'partnerRoles',
                pageName: "Partner Roles",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/partnerRoles",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        // set event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deletePartnerRole);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    componentDidMount = async () => {
        // get parameter from URL
        const partnerRoleId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create partner
        if (partnerRoleId !== undefined) {
            const cancelToken = axios.CancelToken;
            const src = cancelToken.source();

            const url = '/partner-roles?search=%7BpartnerRoleId:' + partnerRoleId + '%7D';

            await API.get(url, {headers: {Authorization: token}, cancelToken: src.token}).then(res => {
                const data = res.data[0];
                oldState.partnerRoleId = (data.partnerRoleId !== undefined) ? data.partnerRoleId : null;
                oldState.partnerRoleName = (data.partnerRoleName !== undefined) ? data.partnerRoleName : '';
                oldState.partnerId = (data.partnerId !== undefined) ? data.partnerId : '';
                oldState.partnerName = (data.partnerName !== undefined) ? data.partnerName : '';
                oldState.token = token;
                oldState.isDisplayLoader = false;

                this.setState(oldState);
            }).catch(er => {
                oldState.isDisplayLoader = false;
                this.setState(oldState);
                this.displayErrorsForAPICalls(er);
            });

            src.cancel();
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        // remove all configured event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deletePartnerRole);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.partnerRoleId !== null) {
            data = {
                partnerRoleId: this.state.partnerRoleId,
                partnerRoleName: this.state.partnerRoleName,
                partnerId: toInteger(this.state.partnerId)
            };
        } else {
            data = {
                partnerId: toInteger(this.state.partnerId),
                partnerRoleName: this.state.partnerRoleName
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.partnerRoleId !== null)
                this.updatePartnerRole(data);
            else {
                this.createPartnerRole(data);
            }
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'partnerRoleName':
                if (value === '')
                    error[key] = "Partner Role Name is Required!";
                else if (value === ' ')
                    error[key] = "Partner Role Name can't be an empty space!";
                else if ((new TextEncoder().encode(value)).length > 32)
                    error[key] = "Partner Role Name can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'partnerId':
                if (this.state.partnerId === '' || this.state.partnerId === null)
                    error[key] = "Please select a Partner!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * create partner role
     * @param data - the data to create a new partner role
     */
    createPartnerRole = async (data) => {
        await API.post('/partner-roles',
            {
                "partnerRoleName": data.partnerRoleName,
                "partnerId": data.partnerId
            }, {headers: {Authorization: this.state.token}}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Partner Role");
            this.props.history.push('/home/partnerRoles');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * update partner role
     * @param data - updated partner role details
     */
    updatePartnerRole = async (data) => {
        await API.put('/partner-roles',
            {
                "partnerRoleId": data.partnerRoleId,
                "partnerRoleName": data.partnerRoleName,
                "partnerId": data.partnerId
            },{headers: {Authorization: this.state.token}}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Partner Role");
            this.props.history.push('/home/partnerRoles');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.partnerRoleId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'partnerRoles',
                name: this.state.partnerRoleName
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Partner Role data",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a partner role
     * @param data
     */
    deletePartnerRole = async (data) => {
        if (data && (this.props.match.path === "/home/partnerRoles/:id")) {
            await API.delete('/partner-roles?partnerRoleId=' + this.state.partnerRoleId, {headers: {Authorization: this.state.token}})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the Partner Role");
                    this.props.history.push('/home/partnerRoles');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Partner Role is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.partnerRoleId = null;
        newState.partnerRoleName = "";
        newState.partnerId = "";
        newState.partnerName = "";
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home/partnerRoles');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default PartnerRoleForm;
